import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { LicenseRequest, LicenseRequestStatus } from "@acruxsoft/platform-shared";
import { adminDismissLicenseRequest, adminListLicenseRequests } from "../lib/callables";
import { Spinner } from "../components/spinner";

const STATUSES: LicenseRequestStatus[] = ["pending", "granted", "dismissed"];

/**
 * License requests, newest first. Granting happens on the user's page — that's
 * where the end date gets picked and where the rest of their access is in
 * view — so "Give access" links there instead of granting from the list.
 */
export function AdminRequestsPage() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<LicenseRequestStatus>("pending");
  const [requests, setRequests] = useState<LicenseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async (s: LicenseRequestStatus) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminListLicenseRequests({ status: s });
      setRequests(data.requests);
    } catch (e) {
      console.error("[admin-requests] load failed", e);
      setError(t("adminPanel.requests.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(status);
  }, [status, load]);

  async function dismiss(r: LicenseRequest) {
    if (!confirm(t("adminPanel.detail.apps.confirmDismiss"))) return;
    const key = `${r.uid}_${r.appId}`;
    setBusyKey(key);
    try {
      await adminDismissLicenseRequest({ uid: r.uid, appId: r.appId });
      setRequests((prev) => prev.filter((x) => `${x.uid}_${x.appId}` !== key));
    } catch (e) {
      console.error("[admin-requests] dismiss failed", e);
      setError(t("adminPanel.detail.err.action"));
    } finally {
      setBusyKey(null);
    }
  }

  const fmt = (ms: number) =>
    new Date(ms).toLocaleString(i18n.language, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }} className="acx__stack acx__stack--lg">
      <header className="acx__stack" style={{ alignItems: "flex-start" }}>
        <Link to="/admin" className="acx__btn acx__btn--ghost">{t("adminPanel.detail.back")}</Link>
        <div>
          <span className="acx__card-eyebrow">{t("adminPanel.eyebrow")}</span>
          <h1 className="acx__home-title">{t("adminPanel.requests.title")}</h1>
        </div>
      </header>

      <div className="acx__chip-row" role="group" aria-label={t("adminPanel.requests.filterLabel")}>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`acx__chip${status === s ? " acx__chip--active" : ""}`}
            aria-pressed={status === s}
          >
            {t(`adminPanel.requests.status.${s}`)}
          </button>
        ))}
      </div>

      {error && <div className="acx__alert acx__alert--danger" role="alert">{error}</div>}

      {loading ? (
        <Spinner />
      ) : requests.length === 0 ? (
        <p style={{ color: "var(--acx-text-muted)" }}>{t(`adminPanel.requests.empty.${status}`)}</p>
      ) : (
        <div className="acx__table-wrap">
          <table className="acx__admin-table">
            <thead>
              <tr>
                <th>{t("adminPanel.requests.col.date")}</th>
                <th>{t("adminPanel.requests.col.user")}</th>
                <th>{t("adminPanel.requests.col.app")}</th>
                <th>{t("adminPanel.requests.col.company")}</th>
                <th className="acx__col--hide-sm">{t("adminPanel.requests.col.message")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => {
                const key = `${r.uid}_${r.appId}`;
                return (
                  <tr key={key}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {fmt(r.updatedAt)}
                      {r.notifiedAt === null && r.status === "pending" && (
                        <div style={{ color: "var(--acx-warning)", fontSize: "var(--acx-fs-xs)" }}>
                          {t("adminPanel.requests.notNotified")}
                        </div>
                      )}
                    </td>
                    <td>
                      <div>{r.email}</div>
                      {r.displayName && <div style={{ color: "var(--acx-text-muted)", fontSize: "var(--acx-fs-xs)" }}>{r.displayName}</div>}
                    </td>
                    <td>{t(`home.apps.${r.appId}.name`)}</td>
                    <td>
                      <div>{r.company}</div>
                      {r.phone && <div style={{ color: "var(--acx-text-muted)", fontSize: "var(--acx-fs-xs)" }}>{r.phone}</div>}
                    </td>
                    <td className="acx__col--hide-sm" style={{ maxWidth: 320, whiteSpace: "pre-wrap" }}>{r.message ?? "—"}</td>
                    <td>
                      <div className="acx__row" style={{ gap: "var(--acx-s-2)", justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <Link to={`/admin/users/${r.uid}#apps`} className="acx__btn acx__btn--primary">
                          {r.status === "pending" ? t("adminPanel.requests.give") : t("adminPanel.list.view")}
                        </Link>
                        {r.status === "pending" && (
                          <button type="button" onClick={() => dismiss(r)} disabled={busyKey === key} className="acx__btn acx__btn--ghost">
                            {t("adminPanel.detail.apps.dismiss")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
